// AttendanceTable.jsx
import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import { uk } from 'date-fns/locale/uk';
import ChildrenTable from '../TableChildren';

import 'react-datepicker/dist/react-datepicker.css';

import { 
  getAttendanceData, 
  saveAttendance 
} from './actions';
import { normalizeDate, getISODate } from './utils/dateUtils';

registerLocale('uk', uk);

const isPossible = (periodStartYear) => {
  // Вікно доступу прив'язане до periodStartYear ГРУПИ, а не до
  // поточного навчального року — інакше 1 вересня (коли curPeriod
  // перескакує на новий рік) доступ до груп МИНУЛОГО року миттєво
  // блокується, попри те що бекенд (tabel.js) окремо дозволяє
  // коригування ще кілька днів у новому місяці.
  const now = new Date();
  const correctionStart = new Date(periodStartYear, 7, 1);       // 1 серпня початку періоду групи
  const correctionEnd = new Date(periodStartYear + 1, 8, 5);     // 5 вересня наступного року (grace period)

  if (now < correctionStart) return false;
  if (now > correctionEnd) return false;
  return true;
};

const AttendanceTable = memo(({
  kg_id,
  gr_id,
  groups = [],
  onSaveAttendance,
  onGetAttendanceData,
  periodStartYear,
  selectedDate,
  mode,
  closePar,
  showIdGroup
}) => {
  const [childrenList, setChildrenList] = useState([]);
  const [isChildrenListReady, setIsChildrenListReady] = useState(false);
  const [attendanceData, setAttendanceData] = useState({});
  const [isCorrection, setIsCorrection] = useState(false);
  const [correctionDate, setCorrectionDate] = useState(() => {
    const initialDate = selectedDate ? new Date(selectedDate) : new Date();
    return normalizeDate(initialDate);
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const isFirstRender = React.useRef(true);

  // Межі навчального року ГРУПИ — однакові для обох режимів: дата
  // ніколи не може виходити за межі власного навчального року групи
  // (1 серпня periodStartYear .. 31 серпня periodStartYear+1). Модуль
  // може лишатись доступним ще кілька днів у вересні (див. isPossible),
  // але сама дата коригування завжди обмежена 31 серпня — коригувати
  // "вересневі" дані старої групи неможливо, бо їх не існує.
  const yearStart = useMemo(() => new Date(periodStartYear, 7, 1), [periodStartYear]);
  const yearEnd = useMemo(() => new Date(periodStartYear + 1, 7, 31, 23, 59, 59, 999), [periodStartYear]);

  // Верхня межа для DatePicker: не пізніше сьогодні, і не пізніше
  // останнього дня навчального року групи — що з них раніше.
  const maxSelectableDate = useMemo(() => {
    const today = normalizeDate(new Date());
    return today < yearEnd ? today : yearEnd;
  }, [yearEnd]);

  const validateCorrectionDate = useCallback((date) => {
    if (!periodStartYear) return false;

    const dateToCheck = normalizeDate(date);

    if (dateToCheck < yearStart) return false;
    if (dateToCheck > yearEnd) return false;
    if (isCorrection && dateToCheck > normalizeDate(new Date())) return false; // не можна коригувати майбутнє

    return true;
  }, [periodStartYear, isCorrection, yearStart, yearEnd]);

  const loadAttendanceData = useCallback(async () => {
    if (!kg_id || !gr_id) return;
    
    setIsLoading(true);
    try {
      const dateToUse = isCorrection ? correctionDate : normalizeDate(new Date());
      const data = await getAttendanceData(kg_id, gr_id, dateToUse, isCorrection ? "correction" : "day");

      const info_att = {};
      if (data && data.data) {
        for (let d of data.data) {
          if (!info_att[d.idChild]) info_att[d.idChild] = true;
        }
      }

      setAttendanceData(info_att);
    } catch (error) {
      console.error('Error loading attendance data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [kg_id, gr_id, isCorrection, correctionDate]);

  const onChildrensList = useCallback((data) => {
    if (!data) return;
    setChildrenList(data);
    setIsChildrenListReady(true);
  }, []);

  const attendanceDataOut = useMemo(() => {
    return childrenList.map(r => ({
      ...r,
      idChild: r.idChild || r._id,
      checked: !!attendanceData[r.idChild || r._id]
    }));
  }, [childrenList, attendanceData]);

  const saveAttendance_ = useCallback(async () => {
    try {
      const dateToUse = normalizeDate(isCorrection ? correctionDate : new Date());
      
      if (isCorrection) {
        const nowNormalized = normalizeDate(new Date());
        if (dateToUse >= nowNormalized) {
          alert('Коригування даних на поточну або майбутню дату неможливо.');
          return;
        }
      }

      const attenGroupObj = {
        date: getISODate(dateToUse),
        idKg: kg_id,
        idGr: gr_id,
        period: isCorrection ? 'correction' : 'day',
        child: attendanceDataOut.filter(r => r.checked).map(r => ({
          idChild: r.idChild || r._id, 
          tPl: r.tPl
        }))
      };

      await saveAttendance(attenGroupObj);

      if (onSaveAttendance) {
        await onSaveAttendance({
          date: dateToUse,
          childIds: Object.keys(attendanceData).filter(id => attendanceData[id]),
          isCorrection
        });
      }

      const checkedCount = Object.keys(attendanceData).filter(id => attendanceData[id]).length;
      alert(`Операція успішна. Зафіксовано відвідування ${checkedCount} дітей`);
      
      if (isCorrection) {
        const nextDate = new Date(correctionDate);
        nextDate.setDate(nextDate.getDate() + 1);
        if (validateCorrectionDate(nextDate)) {
          setIsChildrenListReady(false);
          setCorrectionDate(normalizeDate(nextDate));
        }
      } else {
        loadAttendanceData();
      }
    } catch (error) {
      alert('Виникла помилка при збереженні.');
      console.error('Error saving attendance:', error);
    }
  }, [
    attendanceData, 
    attendanceDataOut, 
    isCorrection, 
    correctionDate, 
    kg_id, 
    gr_id, 
    onSaveAttendance, 
    validateCorrectionDate, 
    loadAttendanceData
  ]);

  const toggleChildAttendance = useCallback((child) => {
    const childId = child._id;
    setAttendanceData(prev => ({
      ...prev,
      [childId]: !prev[childId]
    }));
  }, []);

  const selectAllChildren = useCallback(() => {
    const outChecked = {};
    childrenList.forEach(r => {
      outChecked[r.idChild || r._id] = true;
    });
    setAttendanceData(outChecked);
  }, [childrenList]);

  const deselectAllChildren = useCallback(() => {
    setAttendanceData({});
  }, []);

  const handleDateChange = useCallback((date) => {
    if (date && validateCorrectionDate(date)) {
      setIsChildrenListReady(false);
      setCorrectionDate(normalizeDate(date));
    } else {
      alert('Оберіть дату меншу за поточну або в межах дозволеного періоду');
    }
  }, [validateCorrectionDate]);

  const goToPreviousDate = useCallback(() => {
    const newDate = new Date(correctionDate);
    newDate.setDate(newDate.getDate() - 1);
    if (validateCorrectionDate(newDate)) {
      setIsChildrenListReady(false);
      setCorrectionDate(normalizeDate(newDate));
    }
  }, [correctionDate, validateCorrectionDate]);

  const goToNextDate = useCallback(() => {
    const newDate = new Date(correctionDate);
    newDate.setDate(newDate.getDate() + 1);
    if (validateCorrectionDate(newDate)) {
      setIsChildrenListReady(false);
      setCorrectionDate(normalizeDate(newDate));
    }
  }, [correctionDate, validateCorrectionDate]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      loadAttendanceData();
      setIsInitialized(true);
      return;
    }
    
    if (isInitialized) {
      loadAttendanceData();
    }
  }, [loadAttendanceData, isInitialized]);

  useEffect(() => {
    if (selectedDate && !isCorrection) {
      const newDate = normalizeDate(selectedDate);
      if (newDate.getTime() !== correctionDate.getTime()) {
        setCorrectionDate(newDate);
      }
    }
  }, [selectedDate, isCorrection, correctionDate]);

  const currentGroup = useMemo(() => 
    groups.find(g => g._id === gr_id) || {}, 
    [groups, gr_id]
  );

  const attendanceColumns = useMemo(() => [
    {
      key: 'attendance',
      label: 'Присутність',
      sortable: false,
      width: '10%',
      renderHeader: () => (
        <div className="d-flex align-items-center">
          <input
            type="checkbox"
            onChange={() => {}}
            title="Присутність"
          />
        </div>
      ),
      renderCell: (child) => (
        <input
          type="checkbox"
          checked={!!attendanceData[child._id]}
          onChange={() => toggleChildAttendance(child)}
          disabled={!validateCorrectionDate(correctionDate)}
          className="attendance-checkbox"
        />
      )
    },
    {
      key: 'lastName',
      label: 'Прізвище',
      sortable: true,
      width: '12%'
    },
    {
      key: 'firstName',
      label: "Ім'я",
      sortable: true,
      width: '10%'
    },
    {
      key: 'middleName',
      label: 'По батькові',
      sortable: true,
      width: '12%'
    },
    {
      key: 'birthDate',
      label: 'Дата народження',
      sortable: true,
      width: '10%'
    },
    {
      key: 'documentNumber',
      label: 'Серія та номер свідоцтва про народження',
      sortable: true,
      width: '12%'
    },
    {
      key: 'enrollmentDate',
      label: 'Дата зарахування',
      sortable: true,
      width: '10%'
    },
    {
      key: 'placeType',
      label: 'Тип місця в групі',
      sortable: true,
      width: '10%'
    },
    {
      key: 'foodType',
      label: 'Тип харчування',
      sortable: true,
      width: '12%'
    }
  ], [attendanceData, toggleChildAttendance]);

  if (!isPossible(periodStartYear)) {
    return (
      <div>
        <span>Модуль обліку відвідування недоступний для навчального періоду групи</span>
      </div>
    );
  }

  const isSaveDisabled = isLoading || !isChildrenListReady;

  return (
    <div className="attendance-table-container">
      <div className="attendance-header mb-3">
        <div className="row align-items-center">
          <div className="col-md-8">
            <h5 className="mb-1">Облік відвідування закладу</h5>
            {isCorrection && (
              <p className="text-warning mb-0">
                Увага - включений режим коригування на {correctionDate.toLocaleDateString('uk-UA')}
              </p>
            )}
            <p className="text-muted mb-0">
              Встановіть позначки для дітей, що відвідали садочок {isCorrection ? 'у вибрану дату' : 'сьогодні'}
            </p>
          </div>
          
          <div className="col-md-4">
            <div className="d-flex justify-content-end gap-2">
              {!isCorrection && (
                <button 
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setIsCorrection(true)}
                >
                  Коригування
                </button>
              )}
              
              {isCorrection && (
                <div className="d-flex align-items-center gap-2">
                  <button 
                    className="btn btn-outline-secondary btn-sm"
                    onClick={goToPreviousDate}
                    title="Попередня дата"
                  >
                    ←
                  </button>
                  
                  <DatePicker
                    selected={correctionDate}
                    onChange={handleDateChange}
                    dateFormat="dd.MM.yyyy"
                    locale="uk"
                    className="form-control form-control-sm correction-date"
                    minDate={yearStart}
                    maxDate={maxSelectableDate}
                  />
                  
                  <button 
                    className="btn btn-outline-secondary btn-sm"
                    onClick={goToNextDate}
                    title="Наступна дата"
                  >
                    →
                  </button>
                  
                  <button 
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setIsCorrection(false)}
                  >
                    Звичайний режим
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="attendance-controls mb-3">
        <div className="d-flex gap-2">
          <button 
            className="btn btn-outline-primary btn-sm"
            onClick={selectAllChildren}
            disabled={!isChildrenListReady}
          >
            Обрати всіх
          </button>
          <button 
            className="btn btn-outline-secondary btn-sm"
            onClick={deselectAllChildren}
            disabled={!isChildrenListReady}
          >
            Зняти всі
          </button>
          <button 
            className="btn btn-primary btn-sm"
            onClick={saveAttendance_}
            disabled={isSaveDisabled}
          >
            {isLoading ? 'Збереження...' : (isCorrection ? 'Зберегти коригування' : 'Зафіксувати відвідування')}
          </button>
        </div>
      </div>

      <ChildrenTable
        id={kg_id}
        group_id={gr_id}
        columnsConfig={attendanceColumns}
        onChildrensList={onChildrensList}
        customProps={{
          attendanceData,
          onToggleAttendance: toggleChildAttendance
        }}
        mode='attendance'
        selectedDate={getISODate(correctionDate)}
      />
    </div>
  );
});

AttendanceTable.displayName = 'AttendanceTable';

export default AttendanceTable;
export { AttendanceTable as AttendanceGroup };